/**
 * Platform Configuration Loader
 *
 * Loads and parses platform.config.yml with environment variable substitution
 */

import yaml from "js-yaml";

import debugLogger from "../utils/debugLogger";

import { PlatformConfig } from "./types";

/**
 * Loads platform configuration from YAML file
 * @param filePath Path to the platform.config.yml file (relative to public/)
 *                 typically `/config/platform.config.yml`
 * @returns Parsed platform configuration
 */
export async function loadPlatformConfig(
  filePath: string,
): Promise<PlatformConfig> {
  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(
        `Failed to load platform config from ${filePath}: ${response.statusText}`,
      );
    }
    const fileContents = await response.text();
    const parsedConfig = yaml.load(fileContents) as PlatformConfig;

    // Simple environment variable substitution (e.g., ${VAR_NAME})
    const stringifiedConfig = JSON.stringify(parsedConfig);
    const substitutedConfig = stringifiedConfig.replace(
      /\$\{(\w+)\}/g,
      (match, varName) => {
        return import.meta.env[varName] || "";
      },
    );

    const config = JSON.parse(substitutedConfig) as PlatformConfig;

    // Validate configuration structure
    validatePlatformConfig(config);

    return config;
  } catch (e) {
    debugLogger.error(`Error loading platform config from ${filePath}:`, e);
    throw e;
  }
}

/**
 * Validates platform configuration structure
 * @param config Platform configuration to validate
 */
function validatePlatformConfig(config: PlatformConfig): void {
  if (!config.platform) {
    throw new Error("Invalid platform config: missing platform section");
  }

  if (
    !config.platform.businessDomains ||
    !Array.isArray(config.platform.businessDomains)
  ) {
    throw new Error(
      "Invalid platform config: missing or invalid businessDomains array",
    );
  }

  if (
    !config.platform.maturityLevels ||
    !Array.isArray(config.platform.maturityLevels)
  ) {
    throw new Error(
      "Invalid platform config: missing or invalid maturityLevels array",
    );
  }

  if (
    !config.platform.featureFlags ||
    !Array.isArray(config.platform.featureFlags)
  ) {
    throw new Error(
      "Invalid platform config: missing or invalid featureFlags array",
    );
  }

  // Validate business domains
  config.platform.businessDomains.forEach((domain, domainIndex) => {
    if (!domain.name) {
      throw new Error(
        `Invalid platform config: business domain at index ${domainIndex} missing name`,
      );
    }

    if (!domain.features || !Array.isArray(domain.features)) {
      throw new Error(
        `Invalid platform config: business domain '${domain.name}' missing or invalid features array`,
      );
    }

    // Validate features
    domain.features.forEach((feature, featureIndex) => {
      if (!feature.name) {
        throw new Error(
          `Invalid platform config: feature at index ${featureIndex} in domain '${domain.name}' missing name`,
        );
      }

      if (!feature.components || !Array.isArray(feature.components)) {
        throw new Error(
          `Invalid platform config: feature '${feature.name}' in domain '${domain.name}' missing or invalid components array`,
        );
      }

      if (!feature.routes || !Array.isArray(feature.routes)) {
        throw new Error(
          `Invalid platform config: feature '${feature.name}' in domain '${domain.name}' missing or invalid routes array`,
        );
      }

      // Validate components
      feature.components.forEach((component, componentIndex) => {
        if (!component.name) {
          throw new Error(
            `Invalid platform config: component at index ${componentIndex} in feature '${feature.name}' missing name`,
          );
        }
        if (!component.path) {
          throw new Error(
            `Invalid platform config: component '${component.name}' in feature '${feature.name}' missing path`,
          );
        }
      });

      // Validate routes
      feature.routes.forEach((route, routeIndex) => {
        if (!route.path) {
          throw new Error(
            `Invalid platform config: route at index ${routeIndex} in feature '${feature.name}' missing path`,
          );
        }
        if (!route.component) {
          throw new Error(
            `Invalid platform config: route at index ${routeIndex} in feature '${feature.name}' missing component`,
          );
        }
      });
    });
  });
}
